import { defineStore } from 'pinia'
import { computed, ref, watch, type Ref } from 'vue'

import {
  FeaturesNames,
  PackageOfServices,
  PermissionGroup,
  PermissionGroupUpdateItem,
  ServicesList,
} from '@/shared/types/settings'
import { FetchResponse } from '@/shared/utils/fetch'
import { isStageOrDevelopment } from '@/shared/utils/import-meta'

import {
  FeatureAccessOverride,
  FEATURE_PROVISION_MAP,
  FeatureProvisionField,
  featureFromGuid,
  isProvisionOverride,
  overlayPackages,
  overlayPermissionGroups,
  overlayServices,
} from './feature-access-provision'

export type { FeatureAccessOverride } from './feature-access-provision'
export { DEV_PACKAGE_GUID, featureFromGuid } from './feature-access-provision'

const STORAGE_KEY = 'dev-feature-access-emulator'

export type FeatureAccessEmulatorSnapshot = {
  isActive: boolean
  webSoftphone: FeatureAccessOverride
  instantMessaging: FeatureAccessOverride
  cdrsAccess: FeatureAccessOverride
  webCallBackorder: FeatureAccessOverride
  webToFax: FeatureAccessOverride
  forward: FeatureAccessOverride
  blackWhiteList: FeatureAccessOverride
  voiceMailBoxAccess: FeatureAccessOverride
  accessGroupManagement: FeatureAccessOverride
  doNotDisturb: FeatureAccessOverride
}

const DEFAULT_SNAPSHOT: FeatureAccessEmulatorSnapshot = {
  isActive: false,
  webSoftphone: 'api',
  instantMessaging: 'api',
  cdrsAccess: 'api',
  webCallBackorder: 'api',
  webToFax: 'api',
  forward: 'api',
  blackWhiteList: 'api',
  voiceMailBoxAccess: 'api',
  accessGroupManagement: 'api',
  doNotDisturb: 'api',
}

export const FEATURE_ACCESS_EMULATOR_PRESETS: Record<string, FeatureAccessEmulatorSnapshot> = {
  api: { ...DEFAULT_SNAPSHOT },
  cabinetOnly: {
    ...DEFAULT_SNAPSHOT,
    isActive: true,
    webSoftphone: 'off',
    instantMessaging: 'off',
    cdrsAccess: 'off',
  },
  phoneOnly: {
    ...DEFAULT_SNAPSHOT,
    isActive: true,
    webSoftphone: 'on',
    instantMessaging: 'off',
  },
  fullUc: {
    ...DEFAULT_SNAPSHOT,
    isActive: true,
    webSoftphone: 'on',
    instantMessaging: 'on',
    cdrsAccess: 'on',
    webCallBackorder: 'on',
    webToFax: 'on',
    forward: 'on',
    blackWhiteList: 'on',
    voiceMailBoxAccess: 'on',
  },
  wui3794: {
    ...DEFAULT_SNAPSHOT,
    isActive: true,
    accessGroupManagement: 'package',
    doNotDisturb: 'group',
    forward: 'group',
  },
}

const resolveOverride = (override: FeatureAccessOverride, apiValue: boolean): boolean => {
  if (override === 'on' || override === 'package' || override === 'group') return true
  if (override === 'off') return false
  return apiValue
}

const readSnapshot = (): FeatureAccessEmulatorSnapshot => {
  if (!isStageOrDevelopment() || typeof localStorage === 'undefined') {
    return { ...DEFAULT_SNAPSHOT }
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_SNAPSHOT }
    const parsed = JSON.parse(raw) as Partial<FeatureAccessEmulatorSnapshot>
    return {
      isActive: parsed.isActive ?? DEFAULT_SNAPSHOT.isActive,
      webSoftphone: parsed.webSoftphone ?? DEFAULT_SNAPSHOT.webSoftphone,
      instantMessaging: parsed.instantMessaging ?? DEFAULT_SNAPSHOT.instantMessaging,
      cdrsAccess: parsed.cdrsAccess ?? DEFAULT_SNAPSHOT.cdrsAccess,
      webCallBackorder: parsed.webCallBackorder ?? DEFAULT_SNAPSHOT.webCallBackorder,
      webToFax: parsed.webToFax ?? DEFAULT_SNAPSHOT.webToFax,
      forward: parsed.forward ?? DEFAULT_SNAPSHOT.forward,
      blackWhiteList: parsed.blackWhiteList ?? DEFAULT_SNAPSHOT.blackWhiteList,
      voiceMailBoxAccess: parsed.voiceMailBoxAccess ?? DEFAULT_SNAPSHOT.voiceMailBoxAccess,
      accessGroupManagement: parsed.accessGroupManagement ?? DEFAULT_SNAPSHOT.accessGroupManagement,
      doNotDisturb: parsed.doNotDisturb ?? DEFAULT_SNAPSHOT.doNotDisturb,
    }
  } catch {
    return { ...DEFAULT_SNAPSHOT }
  }
}

const noContent = (): FetchResponse<unknown> => ({
  data: undefined,
  status: 204,
  statusText: 'No Content',
  headers: {},
})

export const useFeatureAccessEmulatorStore = defineStore('feature-access-emulator', () => {
  const isActive = ref(DEFAULT_SNAPSHOT.isActive)
  const webSoftphone = ref<FeatureAccessOverride>(DEFAULT_SNAPSHOT.webSoftphone)
  const instantMessaging = ref<FeatureAccessOverride>(DEFAULT_SNAPSHOT.instantMessaging)
  const cdrsAccess = ref<FeatureAccessOverride>(DEFAULT_SNAPSHOT.cdrsAccess)
  const webCallBackorder = ref<FeatureAccessOverride>(DEFAULT_SNAPSHOT.webCallBackorder)
  const webToFax = ref<FeatureAccessOverride>(DEFAULT_SNAPSHOT.webToFax)
  const forward = ref<FeatureAccessOverride>(DEFAULT_SNAPSHOT.forward)
  const blackWhiteList = ref<FeatureAccessOverride>(DEFAULT_SNAPSHOT.blackWhiteList)
  const voiceMailBoxAccess = ref<FeatureAccessOverride>(DEFAULT_SNAPSHOT.voiceMailBoxAccess)
  const accessGroupManagement = ref<FeatureAccessOverride>(DEFAULT_SNAPSHOT.accessGroupManagement)
  const doNotDisturb = ref<FeatureAccessOverride>(DEFAULT_SNAPSHOT.doNotDisturb)

  const isAvailable = computed(() => isStageOrDevelopment())

  const provisionFields: Record<FeatureProvisionField, Ref<FeatureAccessOverride>> = {
    instantMessaging,
    cdrsAccess,
    webCallBackorder,
    webToFax,
    forward,
    blackWhiteList,
    voiceMailBoxAccess,
    accessGroupManagement,
    doNotDisturb,
  }

  const getProvisions = () => {
    const provisions: Partial<Record<FeaturesNames, FeatureAccessOverride>> = {}
    ;(Object.keys(FEATURE_PROVISION_MAP) as FeatureProvisionField[]).forEach((field) => {
      const value = provisionFields[field].value
      if (isProvisionOverride(value)) {
        provisions[FEATURE_PROVISION_MAP[field]] = value
      }
    })
    return provisions
  }

  const hasProvisionOverride = computed(() => Object.keys(getProvisions()).length > 0)

  const isIntercepting = computed(() => isAvailable.value && isActive.value && hasProvisionOverride.value)

  const hydrate = () => {
    if (!isAvailable.value) return
    const snapshot = readSnapshot()
    isActive.value = snapshot.isActive
    webSoftphone.value = snapshot.webSoftphone
    instantMessaging.value = snapshot.instantMessaging
    cdrsAccess.value = snapshot.cdrsAccess
    webCallBackorder.value = snapshot.webCallBackorder
    webToFax.value = snapshot.webToFax
    forward.value = snapshot.forward
    blackWhiteList.value = snapshot.blackWhiteList
    voiceMailBoxAccess.value = snapshot.voiceMailBoxAccess
    accessGroupManagement.value = snapshot.accessGroupManagement
    doNotDisturb.value = snapshot.doNotDisturb
  }

  hydrate()

  watch([
    isActive,
    webSoftphone,
    instantMessaging,
    cdrsAccess,
    webCallBackorder,
    webToFax,
    forward,
    blackWhiteList,
    voiceMailBoxAccess,
    accessGroupManagement,
    doNotDisturb,
  ], () => {
    if (!isAvailable.value) return
    const snapshot: FeatureAccessEmulatorSnapshot = {
      isActive: isActive.value,
      webSoftphone: webSoftphone.value,
      instantMessaging: instantMessaging.value,
      cdrsAccess: cdrsAccess.value,
      webCallBackorder: webCallBackorder.value,
      webToFax: webToFax.value,
      forward: forward.value,
      blackWhiteList: blackWhiteList.value,
      voiceMailBoxAccess: voiceMailBoxAccess.value,
      accessGroupManagement: accessGroupManagement.value,
      doNotDisturb: doNotDisturb.value,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
  })

  const resolveFeatureOverride = (
    override: FeatureAccessOverride,
    apiValue: boolean,
  ): boolean => {
    if (!isAvailable.value || !isActive.value) return apiValue
    return resolveOverride(override, apiValue)
  }

  const resolveWebSoftphone = (apiValue: boolean): boolean =>
    resolveFeatureOverride(webSoftphone.value, apiValue)

  const resolveInstantMessaging = (apiValue: boolean): boolean =>
    resolveFeatureOverride(instantMessaging.value, apiValue)

  const resolveCdrsAccess = (apiValue: boolean): boolean =>
    resolveFeatureOverride(cdrsAccess.value, apiValue)

  const resolveWebCallBackorder = (apiValue: boolean): boolean =>
    resolveFeatureOverride(webCallBackorder.value, apiValue)

  const resolveWebToFax = (apiValue: boolean): boolean =>
    resolveFeatureOverride(webToFax.value, apiValue)

  const resolveForward = (apiValue: boolean): boolean =>
    resolveFeatureOverride(forward.value, apiValue)

  const resolveBlackWhiteList = (apiValue: boolean): boolean =>
    resolveFeatureOverride(blackWhiteList.value, apiValue)

  const resolveVoiceMailBoxAccess = (apiValue: boolean): boolean =>
    resolveFeatureOverride(voiceMailBoxAccess.value, apiValue)

  const resolveFeature = (feature: FeaturesNames, apiValue: boolean): boolean => {
    switch (feature) {
    case FeaturesNames.INSTANT_MESSAGING:
      return resolveInstantMessaging(apiValue)
    case FeaturesNames.CDRS_ACCESS:
      return resolveCdrsAccess(apiValue)
    case FeaturesNames.WEB_CALL_BACKORDER:
      return resolveWebCallBackorder(apiValue)
    case FeaturesNames.WEB_TO_FAX:
      return resolveWebToFax(apiValue)
    case FeaturesNames.FORWARD:
      return resolveForward(apiValue)
    case FeaturesNames.BLACK_WHITE_LIST:
      return resolveBlackWhiteList(apiValue)
    case FeaturesNames.VOICE_MAILBOX_ACCESS:
      return resolveVoiceMailBoxAccess(apiValue)
    case FeaturesNames.ACCESS_GROUP_MANAGEMENT:
      return resolveFeatureOverride(accessGroupManagement.value, apiValue)
    case FeaturesNames.DO_NOT_DISTURB:
      return resolveFeatureOverride(doNotDisturb.value, apiValue)
    default:
      return apiValue
    }
  }

  const applyPreset = (presetKey: keyof typeof FEATURE_ACCESS_EMULATOR_PRESETS) => {
    const preset = FEATURE_ACCESS_EMULATOR_PRESETS[presetKey]
    if (!preset) return
    isActive.value = preset.isActive
    webSoftphone.value = preset.webSoftphone
    instantMessaging.value = preset.instantMessaging
    cdrsAccess.value = preset.cdrsAccess
    webCallBackorder.value = preset.webCallBackorder
    webToFax.value = preset.webToFax
    forward.value = preset.forward
    blackWhiteList.value = preset.blackWhiteList
    voiceMailBoxAccess.value = preset.voiceMailBoxAccess
    accessGroupManagement.value = preset.accessGroupManagement
    doNotDisturb.value = preset.doNotDisturb
  }

  const reset = () => {
    applyPreset('api')
  }

  const applyGroupsOverlay = <T extends { data: PermissionGroup[] }>(result: T): T => {
    if (!isIntercepting.value || !Array.isArray(result.data)) return result
    return {
      ...result,
      data: overlayPermissionGroups(result.data, getProvisions()),
    }
  }

  const applyServicesOverlay = <T extends { data: ServicesList[] }>(result: T): T => {
    if (!isIntercepting.value || !Array.isArray(result.data)) return result
    return {
      ...result,
      data: overlayServices(result.data, getProvisions()),
    }
  }

  const applyPackagesOverlay = <T extends { data: PackageOfServices[] }>(result: T): T => {
    if (!isIntercepting.value || !Array.isArray(result.data)) return result
    return {
      ...result,
      data: overlayPackages(result.data, getProvisions()),
    }
  }

  const setProvision = (feature: FeaturesNames, value: FeatureAccessOverride) => {
    const field = (Object.keys(FEATURE_PROVISION_MAP) as FeatureProvisionField[]).find((key) => {
      return FEATURE_PROVISION_MAP[key] === feature
    })
    if (!field) return
    provisionFields[field].value = value
  }

  const mockUpdatePermissionGroup = (updateItem: PermissionGroupUpdateItem) => {
    const feature = featureFromGuid(updateItem.guid)
    if (feature) setProvision(feature, updateItem.aclEnabled ? 'group' : 'off')
    return noContent()
  }

  const mockUpdatePackageOfServices = () => noContent()

  return {
    isAvailable,
    isActive,
    isIntercepting,
    webSoftphone,
    instantMessaging,
    cdrsAccess,
    webCallBackorder,
    webToFax,
    forward,
    blackWhiteList,
    voiceMailBoxAccess,
    accessGroupManagement,
    doNotDisturb,
    resolveWebSoftphone,
    resolveInstantMessaging,
    resolveCdrsAccess,
    resolveWebCallBackorder,
    resolveWebToFax,
    resolveForward,
    resolveBlackWhiteList,
    resolveVoiceMailBoxAccess,
    resolveFeature,
    applyPreset,
    reset,
    hydrate,
    applyGroupsOverlay,
    applyServicesOverlay,
    applyPackagesOverlay,
    mockUpdatePermissionGroup,
    mockUpdatePackageOfServices,
  }
})
